// WP-07 — AuthService (LLD-05 §4): login/logout over the ApiClient, writing
// the SessionStore. Tenant resolution is server-side (Host header carries the
// subdomain; companyCode is the D-MT-1 bare-domain fallback). Foremen never
// get an office session (BR-021) — a FOREMAN login result is refused here.
import type { ApiClient } from "../api/api-client";
import type { LoginRequestDto, LoginResultDto } from "../api/contracts";
import { BusinessError } from "../api/errors";
import { toTokens } from "../api/token-store";
import { t } from "../i18n/i18n";
import { applyCanonicalBranding } from "../theme/branding";
import { isOfficeRole } from "./roles";
import { sessionStore, type Session } from "./session-store";

export class AuthService {
  constructor(private readonly api: ApiClient) {}

  async login(
    email: string,
    password: string,
    companyCode?: string,
  ): Promise<Session> {
    const body: LoginRequestDto = companyCode
      ? { email, password, companyCode }
      : { email, password };
    const result = await this.api.post<LoginResultDto>("/auth/login", body);

    if (!isOfficeRole(result.user.role)) {
      // BR-021: the foreman surface is the field app. Do not store a session.
      throw new BusinessError(
        403,
        "FOREMAN_NO_OFFICE_ACCESS",
        t("errors.foremanNoAccess"),
      );
    }

    const session: Session = {
      user: result.user,
      role: result.user.role,
      tokens: toTokens(result),
      org: result.org,
    };
    sessionStore.getState().setSession(session);
    // WP-22 (D-F5): theme the shell app-wide from the tenant's branding — no
    // owner-only fetch, defaults preserved for an un-branded tenant.
    applyCanonicalBranding(result.org?.branding);
    return session;
  }

  /** Best-effort server-side revoke, then always clear the local session. */
  async logout(): Promise<void> {
    const tokens = sessionStore.getState().tokens;
    if (tokens) {
      await this.api
        .post("/auth/revoke", { refreshToken: tokens.refreshToken })
        .catch(() => {
          // Revoke is best-effort: local sign-out must never be blocked.
        });
    }
    sessionStore.getState().clearSession();
  }

  getCurrentUser(): Session | null {
    const { user, role, tokens } = sessionStore.getState();
    return user && role && tokens ? { user, role, tokens } : null;
  }
}
